import React from 'react';
import { TickerDisplayData } from '../types/ticker';
import {
  TickerTechnicalData,
  formatNumber,
  hasGoldenCross,
  hasDeathCross,
  getBollingerBandIcon,
  getFibonacciIcon,
  getValueColor,
} from '../utils/technicalsTabUtils';
import { GoldenCrossIcon, DeathCrossIcon } from '../components/Icons';

export interface UseTechnicalsTabStatisticsOptions {
  tickerTechnicalData: TickerTechnicalData;
  // Optional syncing state handling (for ListTechnicals)
  syncingTickers?: Set<string>;
  unsyncedTickers?: Set<string>;
  pollingLimitReached?: Set<string>;
}

/**
 * Format statistic value for Technicals tab
 */
export const formatTechnicalsStatisticValue = (
  statisticKey: string,
  ticker: TickerDisplayData,
  options: UseTechnicalsTabStatisticsOptions
): React.ReactNode => {
  const { tickerTechnicalData, syncingTickers, unsyncedTickers, pollingLimitReached } = options;
  
  const symbol = ticker.ticker;
  const tickerData = tickerTechnicalData[symbol];
  
  // Handle syncing/loading states (for ListTechnicals)
  if (syncingTickers || unsyncedTickers || pollingLimitReached) {
    const isSyncing = syncingTickers?.has(symbol);
    const isUnsynced = unsyncedTickers?.has(symbol);
    const isPollingLimitReached = pollingLimitReached?.has(symbol);
    
    // Show - if polling limit reached for unsynced tickers
    if (isPollingLimitReached) {
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    }
    
    // Show loading indicator for syncing or unsynced tickers
    if (isSyncing || isUnsynced) {
      return <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>;
    }
  }
  
  if (!tickerData) {
    return <span className="text-gray-400 dark:text-text-secondary">-</span>;
  }

  // Get the appropriate color for this ticker and statistic
  const valueColor = getValueColor(tickerData, statisticKey);

  switch (statisticKey) {
    case 'last_closing_price':
      return tickerData.price ? (
        <span className={valueColor}>${formatNumber(tickerData.price.close_price_adjusted)}</span>
      ) : (
        <span className="text-gray-400 dark:text-text-secondary">-</span>
      );
    
    case 'pct_off_52w_high':
      const high52w = tickerData.technicals['52W_HIGH']?.value;
      const currentPrice = tickerData.price?.close_price_adjusted;
      if (high52w && currentPrice && high52w > 0) {
        const percentage = ((high52w - currentPrice) / high52w) * 100;
        return <span className={valueColor}>{formatNumber(percentage)}%</span>;
      }
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    
    case 'pct_above_52w_low':
      const low52w = tickerData.technicals['52W_LOW']?.value;
      const currentPrice2 = tickerData.price?.close_price_adjusted;
      if (low52w && currentPrice2 && low52w > 0) {
        const percentage = ((currentPrice2 - low52w) / low52w) * 100;
        return <span className={valueColor}>{formatNumber(percentage)}%</span>;
      }
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    
    case 'FIB_SWING_PERIOD':
      // Combine FIB_SWING_START_DATE and FIB_SWING_END_DATE from indicator_non_numeric_value
      const startDateData = tickerData.technicals['FIB_SWING_START_DATE'];
      const endDateData = tickerData.technicals['FIB_SWING_END_DATE'];
      const startDate = startDateData?.nonNumericValue;
      const endDate = endDateData?.nonNumericValue;
      
      // Check if dates exist and are not empty strings
      if (startDate && endDate && startDate.trim() !== '' && endDate.trim() !== '') {
        // Format dates (assuming they're in YYYY-MM-DD format)
        const formatDate = (dateStr: string) => {
          try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) {
              return dateStr; // Return original if invalid date
            }
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          } catch {
            return dateStr;
          }
        };
        return (
          <span className="text-text-light-neutral dark:text-text-secondary block leading-tight">
            <span className="block">{formatDate(startDate)}</span>
            <span className="block">{formatDate(endDate)}</span>
          </span>
        );
      }
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    
    case 'FIB_SWING_PERCENTAGE':
      const swingPercentage = tickerData.technicals[statisticKey]?.value;
      if (swingPercentage !== undefined && swingPercentage !== null) {
        const valueColor = getValueColor(tickerData, statisticKey);
        // Add + for positive, - for negative (down swings)
        const sign = swingPercentage >= 0 ? '+' : '-';
        // Use absolute value for formatting, then add sign
        const absValue = Math.abs(swingPercentage);
        return <span className={valueColor}>{sign}{formatNumber(absValue)}%</span>;
      }
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    
    default:
      const technicalValue = tickerData.technicals[statisticKey]?.value;
      
      if (technicalValue !== undefined && technicalValue !== null) {
        // Format based on the type of indicator
        if (statisticKey.includes('VMA')) {
          // VMA values: round to nearest integer and format with commas
          const roundedValue = Math.round(technicalValue);
          return <span className={valueColor}>{formatNumber(roundedValue, 0)}</span>;
        } else if (statisticKey.includes('RSI')) {
          // RSI values: 2 decimal places with color coding and fire/ice icons
          const rsiValue = technicalValue;
          const valueColor = getValueColor(tickerData, statisticKey);
          
          // Check for fire icon (RSI > 70) or ice icon (RSI < 30)
          let icon = null;
          if (rsiValue > 70) {
            icon = (
              <span className="ml-1 text-red-500" title="Overbought">
                🔥
              </span>
            );
          } else if (rsiValue < 30) {
            icon = (
              <span className="ml-1 text-blue-500" title="Oversold">
                ❄️
              </span>
            );
          }
          
          return (
            <span className={valueColor}>
              {formatNumber(technicalValue)}
              {icon}
            </span>
          );
        } else if (statisticKey.includes('%')) {
          // Other percentage values: 2 decimal places
          return <span className="text-text-secondary">{formatNumber(technicalValue)}</span>;
        } else if (statisticKey.includes('MACD')) {
          // MACD values: 2 decimal places with color coding
          return <span className={valueColor}>{formatNumber(technicalValue)}</span>;
        } else if (statisticKey.startsWith('STOCH_')) {
          // Stochastic Oscillator values: 2 decimal places with color coding and fire/snowflake icons
          const stochValue = technicalValue;
          
          // Check for fire icon (> 80) or snowflake icon (< 20)
          let icon = null;
          if (stochValue > 80) {
            icon = (
              <span className="ml-1 text-red-500" title="Overbought">
                🔥
              </span>
            );
          } else if (stochValue < 20) {
            icon = (
              <span className="ml-1 text-blue-500" title="Oversold">
                ❄️
              </span>
            );
          }
          
          return (
            <span className={valueColor}>
              {formatNumber(technicalValue)}
              {icon}
            </span>
          );
        } else if (statisticKey.startsWith('FIB_') && statisticKey !== 'FIB_SWING_PERIOD' && statisticKey !== 'FIB_SWING_PERCENTAGE') {
          // Fibonacci level values: format as price with $ and 2 decimal places
          if (technicalValue !== null && technicalValue !== undefined) {
            const fibonacciIcon = getFibonacciIcon(tickerData, statisticKey);
            return (
              <span className={`${valueColor} inline-flex items-center whitespace-nowrap`}>
                ${formatNumber(technicalValue)}
                {fibonacciIcon}
              </span>
            );
          }
          return <span className="text-gray-400 dark:text-text-secondary">-</span>;
        } else if (statisticKey.startsWith('BB_')) {
          // Bollinger Bands: format as currency with icons and color
          const bbIcon = getBollingerBandIcon(tickerData, statisticKey);
          const valueColor = getValueColor(tickerData, statisticKey);
          if (technicalValue !== null) {
            return (
              <span className={`${valueColor} inline-flex items-center whitespace-nowrap`}>
                ${formatNumber(technicalValue)}
                {bbIcon}
              </span>
            );
          }
          return <span className="text-gray-400 dark:text-text-secondary">-</span>;
        } else if (statisticKey.includes('SMA') || statisticKey.includes('EMA') || statisticKey === '52W_HIGH' || statisticKey === '52W_LOW') {
          // Price-based indicators: format as currency with commas
          let goldenCrossIcon = null;
          let deathCrossIcon = null;
          
          // Check for golden cross and death cross for specific indicator pairs
          if (statisticKey === 'SMA_20') {
            if (hasGoldenCross(tickerData, 'SMA_20', 'SMA_50')) {
              goldenCrossIcon = (
                <span className="ml-1 group/golden-cross relative">
                  <GoldenCrossIcon className="w-4 h-4" />
                  <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
                    20-Day SMA crossed over 50-Day SMA in the past 5 days
                  </div>
                </span>
              );
            } else if (hasDeathCross(tickerData, 'SMA_20', 'SMA_50')) {
              deathCrossIcon = (
                <span className="ml-1 group/death-cross relative">
                  <DeathCrossIcon className="w-4 h-4" />
                  <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
                    20-Day SMA crossed below 50-Day SMA in the past 5 days
                  </div>
                </span>
              );
            }
          } else if (statisticKey === 'SMA_50') {
            if (hasGoldenCross(tickerData, 'SMA_50', 'SMA_200')) {
              goldenCrossIcon = (
                <span className="ml-1 group/golden-cross relative">
                  <GoldenCrossIcon className="w-4 h-4" />
                  <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
                    50-Day SMA crossed over 200-Day SMA in the past 5 days
                  </div>
                </span>
              );
            } else if (hasDeathCross(tickerData, 'SMA_50', 'SMA_200')) {
              deathCrossIcon = (
                <span className="ml-1 group/death-cross relative">
                  <DeathCrossIcon className="w-4 h-4" />
                  <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
                    50-Day SMA crossed below 200-Day SMA in the past 5 days
                  </div>
                </span>
              );
            }
          } else if (statisticKey === 'SMA_W50') {
            if (hasGoldenCross(tickerData, 'SMA_W50', 'SMA_W200')) {
              goldenCrossIcon = (
                <span className="ml-1 group/golden-cross relative">
                  <GoldenCrossIcon className="w-4 h-4" />
                  <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
                    50-Week SMA crossed over 200-Week SMA in the past 5 days
                  </div>
                </span>
              );
            } else if (hasDeathCross(tickerData, 'SMA_W50', 'SMA_W200')) {
              deathCrossIcon = (
                <span className="ml-1 group/death-cross relative">
                  <DeathCrossIcon className="w-4 h-4" />
                  <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
                    50-Week SMA crossed below 200-Week SMA in the past 5 days
                  </div>
                </span>
              );
            }
          } else if (statisticKey === 'EMA_21') {
            if (hasGoldenCross(tickerData, 'EMA_21', 'EMA_50')) {
              goldenCrossIcon = (
                <span className="ml-1 group/golden-cross relative">
                  <GoldenCrossIcon className="w-4 h-4" />
                  <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
                    21-Day EMA crossed over 50-Day EMA in the past 5 days
                  </div>
                </span>
              );
            } else if (hasDeathCross(tickerData, 'EMA_21', 'EMA_50')) {
              deathCrossIcon = (
                <span className="ml-1 group/death-cross relative">
                  <DeathCrossIcon className="w-4 h-4" />
                  <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
                    21-Day EMA crossed below 50-Day EMA in the past 5 days
                  </div>
                </span>
              );
            }
          } else if (statisticKey === 'EMA_50') {
            if (hasGoldenCross(tickerData, 'EMA_50', 'EMA_200')) {
              goldenCrossIcon = (
                <span className="ml-1 group/golden-cross relative">
                  <GoldenCrossIcon className="w-4 h-4" />
                  <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
                    50-Day EMA crossed over 200-Day EMA in the past 5 days
                  </div>
                </span>
              );
            } else if (hasDeathCross(tickerData, 'EMA_50', 'EMA_200')) {
              deathCrossIcon = (
                <span className="ml-1 group/death-cross relative">
                  <DeathCrossIcon className="w-4 h-4" />
                  <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
                    50-Day EMA crossed below 200-Day EMA in the past 5 days
                  </div>
                </span>
              );
            }
          }
          
          if (technicalValue !== null) {
            return (
              <span className={`${valueColor} inline-flex items-center whitespace-nowrap`}>
                ${formatNumber(technicalValue)}
                {goldenCrossIcon}
                {deathCrossIcon}
              </span>
            );
          }
          return <span className="text-gray-400 dark:text-text-secondary">-</span>;
        } else {
          // Default: 2 decimal places with commas
          if (technicalValue !== null) {
            return <span className="text-text-secondary">{formatNumber(technicalValue)}</span>;
          }
          return <span className="text-gray-400 dark:text-text-secondary">-</span>;
        }
      }
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
  }
};

/**
 * Get statistic color for Technicals tab
 */
export const getTechnicalsStatisticColor = (
  statisticKey: string,
  ticker: TickerDisplayData,
  options: UseTechnicalsTabStatisticsOptions
): string => {
  const { tickerTechnicalData } = options;
  const tickerData = tickerTechnicalData[ticker.ticker];
  return getValueColor(tickerData, statisticKey);
};

